import { DrugStatus } from "@/models/Drug/Drug";
import { DrugProduct, DrugSource } from "@/models/Drug/DrugProduct";
import { AssayBasis } from "@/models/Inventory";
import DrugStatusEnum = DrugStatus.DrugStatusEnum;

export interface CompoundPatEd {
    id: number;
    compoundID: number;
    description: string;
    contraindications: string;
    administration: string;
    missedDoseInstructions: string;
    monitoring: string;
    sideEffects: string;
    storage: string;
}
export class CompoundPatEd {
    constructor(obj?:CompoundPatEd){
        if(obj) Object.assign(this, obj);
    }
}

export interface CompoundIngredients {
    id: number;
    compoundID: number;
    drugSource: number;
    drugID: number;
    quantity: number;
    unitOfMeasure: string;
    drugName: string;
    ndc: string;
    /** Display order on the formula worksheet; order carries meaning for geometric dilution. */
    order: number;
    /** "L" (legend) or "O" (OTC). Null falls back to the GSDD-derived value when printing. */
    legendIndicator: string | null;
    ingredientNote: string | null;
    /**
     * Opt-in: only flagged ingredients (the APIs) have an activity factor computed from their lot's
     * COA, warn on missing COA, or raise the batch review flag. Bases and excipients stay off.
     */
    applyActivityFactor: boolean;
    /**
     * Optional multiplier (e.g. 1.033 for estradiol hemihydrate) that replaces the COA-derived
     * adjustment for this ingredient — the two are never combined.
     */
    fixedConversionFactor: number | null;
}
export class CompoundIngredients {
    constructor(obj?:Array<CompoundIngredients>){
        if(obj) Object.assign(this, obj);
    }
}

export interface CompoundWarningLabels {
    id: number;
    compoundID: number;
    order: number;
    warning: string;
}
export class CompoundWarningLabels {
    constructor(obj?:Array<CompoundWarningLabels>){
        if(obj) Object.assign(this, obj);
    }
}

export interface CompoundSteps {
    id: number;
    compoundID: number;
    stepNumber: number;
    description: string;
    requireWeight: boolean;
    requirePicture: boolean;
    weightTolerance: number | null;
    /** Hand-entered target for one formula yield; null on a step linked to an ingredient, whose target is derived. */
    expectedWeight: number | null;
    requirePharmacistSignOff: boolean;
    /**
     * The formula ingredient this step weighs out, if any. A linked step's expected weight is derived
     * at fill time from that ingredient's effective quantity; the same ingredient may be linked from
     * several ordered steps.
     */
    compoundIngredientID: number | null;
    /** Share of the ingredient's effective quantity this step weighs, as a percentage. Null on the remainder step. */
    proportionPercent: number | null;
    /** This step weighs out the balance the other linked steps leave (quantum satis); at most one per ingredient. */
    isRemainderStep: boolean;
}
export class CompoundSteps {
    constructor(obj?:CompoundSteps){
        if(obj) Object.assign(this, obj);
    }
}

export interface CompoundBatchSteps {
    id: number;
    compoundBatchID: number;
    stepNumber: number;
    description: string;
    expectedWeight: number | null;
    actualWeight: number | null;
    withinTolerance: boolean | null;
    photoId: number | null;
    completed: Date | null;
    completedBy: string | null;
    requirePicture: boolean;
    requireWeight: boolean;
    requirePharmacistSignOff: boolean;
    pharmacistVerified: boolean | null;
    pharmacistVerifiedBy: string | null;
    pharmacistVerifiedDate: Date | null;
    // Snapshot of the formula step's ingredient link when the batch was created. expectedWeight on a
    // linked step is re-derived from the ingredient's effective quantity as it is captured.
    compoundIngredientID: number | null;
    proportionPercent: number | null;
    isRemainderStep: boolean;
    /** Read-only: the linked formula ingredient's current drug name, for the record pages. */
    linkedIngredientName: string | null;
}
export class CompoundBatchSteps {
    constructor(obj?:CompoundBatchSteps){
        if(obj) Object.assign(this, obj);
    }
}

export interface CompoundBatchIngredients {
    id: number;
    compoundBatchID: number;
    drugID: number;
    drugSource: number;
    /**
     * The formula line this row fulfils. Sent on save so the server can tell whether the ingredient
     * is flagged for activity factor even where a preset swapped the drug out.
     */
    compoundIngredientId: number | null;
    /**
     * The effective weigh-out quantity — overrideQuantity where present, otherwise
     * adjustedQuantity — and the value batch completion deducts from inventory.
     */
    quantity: number;
    unitOfMeasure: string;
    lotNumber: string | null;
    expirationDate: Date | null;
    created: Date;
    createdBy: string;
    drugName: string | null;
    /** The exact inventory row (lot) selected for this ingredient. */
    inventoryId: number | null;
    // COA snapshot taken when the ingredient is saved to the batch. Where fixedConversionFactor is
    // set the COA values are recorded but not applied.
    assayPercent: number | null;
    assayBasis: AssayBasis | null;
    lodPercent: number | null;
    fixedConversionFactor: number | null;
    activityFactor: number | null;
    /** Batch-scaled formula quantity before any activity adjustment. */
    scaledQuantity: number | null;
    /** scaledQuantity / activityFactor. */
    adjustedQuantity: number | null;
    // Manual override of the computed weigh-out; who and when are stamped server-side.
    overrideQuantity: number | null;
    overrideReason: string | null;
    overrideBy: string | null;
    overrideDate: Date | null;
    /** Set when a flagged ingredient was filled from a lot carrying no COA (factor 1.0 used). */
    missingCoaFlag: boolean;
}
export class CompoundBatchIngredients {
    constructor(obj?:CompoundBatchIngredients){
        if(obj) Object.assign(this, obj);
    }
}

export enum CompoundBatchStatus {
    InProgress = 0,
    Completed = 1,
    Cancelled = 2,
    AwaitingVerification = 3,
    Rejected = 4
}

export enum CompoundDosageForm {
    NotSpecified = 0,
    Capsule = 1,
    Ointment = 2,
    Cream = 3,
    Suppository = 4,
    Powder = 5,
    Emulsion = 6,
    Liquid = 7,
    Tablet = 10,
    Solution = 11,
    Suspension = 12,
    Lotion = 13,
    Shampoo = 14,
    Elixir = 15,
    Syrup = 16,
    Lozenge = 17,
    Enema = 18
}

const compoundDosageFormNames: Record<number, string> = {
    [CompoundDosageForm.Capsule]: 'Capsule',
    [CompoundDosageForm.Ointment]: 'Ointment',
    [CompoundDosageForm.Cream]: 'Cream',
    [CompoundDosageForm.Suppository]: 'Suppository',
    [CompoundDosageForm.Powder]: 'Powder',
    [CompoundDosageForm.Emulsion]: 'Emulsion',
    [CompoundDosageForm.Liquid]: 'Liquid',
    [CompoundDosageForm.Tablet]: 'Tablet',
    [CompoundDosageForm.Solution]: 'Solution',
    [CompoundDosageForm.Suspension]: 'Suspension',
    [CompoundDosageForm.Lotion]: 'Lotion',
    [CompoundDosageForm.Shampoo]: 'Shampoo',
    [CompoundDosageForm.Elixir]: 'Elixir',
    [CompoundDosageForm.Syrup]: 'Syrup',
    [CompoundDosageForm.Lozenge]: 'Lozenge',
    [CompoundDosageForm.Enema]: 'Enema',
};

export enum CompoundType {
    NotSpecified = 0,
    AntiInfective = 1,
    Ionotropic = 2,
    Chemotherapy = 3,
    PainManagement = 4,
    TpnPpn = 5,
    Hydration = 6,
    Ophthalmic = 7,
    Z0790 = 8,
    Z0791 = 9,
    Z0792 = 10,
    Z0793 = 11,
    Other = 99
}

export enum CompoundLevelOfEffort {
    NotSpecified = 0,
    Level1_Lowest = 11,
    Level2_LowComplexity = 12,
    Level3_ModerateComplexity = 13,
    Level4_HighComplexity = 14,
    Level5_Highest = 15
}

export const CompoundLevelOfEffortDisplayNames = [
    { enum: CompoundLevelOfEffort.NotSpecified, name: 'Not Specified' },
    { enum: CompoundLevelOfEffort.Level1_Lowest, name: 'Level 1 Lowest' },
    { enum: CompoundLevelOfEffort.Level2_LowComplexity, name: 'Level 2 Low Complexity' },
    { enum: CompoundLevelOfEffort.Level3_ModerateComplexity, name: 'Level 3 Moderate Complexity' },
    { enum: CompoundLevelOfEffort.Level4_HighComplexity, name: 'Level 4 High Complexity' },
    { enum: CompoundLevelOfEffort.Level5_Highest, name: 'Level 5 Highest' },
];

/**
 * The kinds of review flag the server derives for a batch. New kinds may appear before this union
 * learns them, so consumers must handle an unknown code (the gate holds regardless).
 */
export type CompoundBatchReviewFlagCode = 'MissingCoa' | 'ManualOverride';

/**
 * A condition on a batch's record that a pharmacist must acknowledge before the batch can be
 * verified. Derived server-side from the batch record — never stored — so it always reflects the
 * rows as saved; the acknowledgement is stored on the batch (reviewAcknowledgedBy / Date). One
 * acknowledgement covers every flag.
 */
export interface CompoundBatchReviewFlag {
    compoundBatchID: number;
    flagCode: CompoundBatchReviewFlagCode | string;
    /** The ingredient row the flag is about; null for flags that are not per ingredient. */
    compoundBatchIngredientID: number | null;
    /** The step row the flag is about; null for flags that are not per step. */
    compoundBatchStepID: number | null;
}

export interface CompoundBatch {
    id: number;
    batchNumber: string;
    compoundID: number;
    batchSize: number;
    packageSize: number | null;
    lotNumber: string | null;
    beyondUseDate: Date | null;
    storeId: number | null;
    rxNumber: number | null;
    rfNumber: number | null;
    status: CompoundBatchStatus;
    verifiedBy: string | null;
    verifiedDate: Date | null;
    /**
     * Pharmacist acknowledgement of the batch's review flags (missing COA, manual override), which
     * gate verification. The flags themselves are derived from the ingredient rows, not stored.
     */
    reviewAcknowledgedBy: string | null;
    reviewAcknowledgedDate: Date | null;
    /** The review flags derived from this batch's record; empty when nothing needs acknowledging, null when the read did not load them. */
    reviewFlags: CompoundBatchReviewFlag[] | null;
    /** True while review flags exist that no pharmacist has acknowledged; verification is refused in this state. */
    requiresReviewAcknowledgement: boolean;
    compound: CompoundDrug | null;
    steps: CompoundBatchSteps[] | null;
    ingredients: CompoundBatchIngredients[] | null;
    created: Date;
    createdBy: string;
    storeName: string | null;
    stepCount: number | null;
    ingredientCount: number | null;
    requiredVerificationCount: number | null;
    completedVerificationCount: number | null;
}
export class CompoundBatch {
    constructor(obj?:CompoundBatch){
        if(obj) Object.assign(this, obj);
    }
}

export interface CompoundDrug extends DrugProduct {
    compoundID?: number;
    ndc: string;
    name: string;
    labelName: string | null;
    awp: number;
    awpManualOverride: boolean;
    packageSize: number;
    unitOfMeasure: string;
    formulaSize: number | null;
    minimumDispenseQuantity: number;
    enforceMinimumDispenseMultiples: boolean;
    deaClass: string;
    auditChance: number;
    instructions: string;
    dosageForm: number | null;
    routeOfAdministration: string | null;
    compoundType: number | null;
    levelOfEffort: number | null;
    status: DrugStatusEnum;
    patEd: CompoundPatEd;
    ingredients: Array<CompoundIngredients>;
    warningLabels: Array<CompoundWarningLabels> | null;
    steps: Array<CompoundSteps> | null;
    // Formula worksheet fields. physicalDescription is the appearance of the finished product;
    // patEd.description is patient-education text and stays separate.
    physicalDescription: string | null;
    equipment: string | null;
    packaging: string | null;
    labeling: string | null;
    flavor: string | null;
    formulaClass: string | null;
    pccaId: string | null;
    dispensingPackSize: string | null;
    daysToExpiration: number | null;
    timeToMakeMinutes: number | null;
    /** Free-text lines printed above the numbered preparation steps. */
    directionsPreamble: string | null;
}

export class CompoundDrug extends DrugProduct {
    constructor(compoundId?:number|null, obj?:CompoundDrug|DrugProduct){
        super(obj);
        this.compoundID = compoundId || 0;
        this.drugId = Number(this.compoundID);
        // this.status = new DrugStatus()
        this.activeStatus = DrugStatusEnum.Inactive;
        this.source = DrugSource.CompoundProduct;

        if(obj){
            Object.assign(this, obj)
            this.compoundID = obj.drugId;
        }
    }
    toString(long: boolean = false): string {
        const productName = this.name;
        let preferred = '';
        if (this.status == DrugStatusEnum.ActiveAndPreferred) {
            preferred = '\u2B50 ';
        }
        const doseFormName = this.dosageForm ? compoundDosageFormNames[this.dosageForm] : null;
        const doseFormDisplay = doseFormName ? ` (${doseFormName})` : '';

        // For hover (long version), show label name when it exists
        if (long && this.labelName) {
            return this.ndc ? `${preferred}(NG) CompoundID:${this.compoundID}, NDC:${this.ndc} - ${productName}${doseFormDisplay} ${this.packageSize ? ("- PS: " + this.packageSize) : ""}${this.quantityOnHand ? ' - QTY:'+this.quantityOnHand : ''} - Label: ${this.labelName}`
                : `${productName}${doseFormDisplay} - Label: ${this.labelName}`;
        }

        if (productName)
            return this.ndc ? `${preferred}(NG) CompoundID:${this.compoundID}, NDC:${this.ndc} - ${productName}${doseFormDisplay} ${this.packageSize ? ("- PS: " + this.packageSize) : ""}${this.quantityOnHand ? ' - QTY:'+this.quantityOnHand : ''}`
                : productName;
        else return '';
    }

    set activeStatus(val: DrugStatusEnum) {
        this.status = val;
    }

    get activeStatus(): DrugStatusEnum {
        return this.status;
    }

    get productNameShort():string {
        return this.name;
    }
    set productNameShort(val: string) {
        this.name = val;
    }

    get isCII():boolean{
        return this.deaClass == "schedule II";
    }

    get NDCFormat542():string{
        return CompoundDrug.formatNDC542(this.ndc);
    }

    static formatNDC542(value:string | null) : string {
        if(!value) return '';
        return `${value.substr(0, 5)}-${value.substr(5, 4)}-${value.substr(9)}`;
    }
}

export interface CompoundPresetIngredient {
    id: number;
    compoundPresetID: number;
    compoundIngredientID: number;
    drugID: number;
    drugSource: number;
    quantity: number;
    unitOfMeasure: string;
    drugName: string | null;
}
export class CompoundPresetIngredient {
    constructor(obj?: CompoundPresetIngredient) {
        if (obj) Object.assign(this, obj);
    }
}

export interface CompoundPreset {
    id: number;
    compoundID: number;
    name: string;
    isDefault: boolean;
    ingredients: CompoundPresetIngredient[];
}
export class CompoundPreset {
    constructor(obj?: CompoundPreset) {
        if (obj) Object.assign(this, obj);
    }
}

export namespace CompoundDrug {
    export class EquivalentCompoundDrug{
        compoundID? : number;
        ndc? : string;
        productName? : string;
        packageSize?: number;
    }
}
